<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useClientRequests } from "../../Composables/useClientRequests.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import { useRouter } from "vue-router"
import _ from 'lodash'
import axios from "axios"
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"
import FormSelectField from "../../Components/FormSelectField.vue"
import FormDatePicker from "../../Components/FormDatePicker.vue"
import Loader from "../../Components/Loader.vue"


// Statuts où la demande est prise en charge (ClientRequestObserver::HANDLED_STATUSES)
const HANDLED_STATUSES = ['responded', 'appointment_scheduled', 'won', 'no_show', 'lost', 'duplicate', 'closed']
const router = useRouter()
const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_ClientRequestList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: clientRequests,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
    updateFilters,
} = useDataManager({
    baseUrl: '/api/client-requests',
    baseSearchUrl: '/api/search/client-requests',
    defaultSortingField: 'updated_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        updated_at: 'desc',
        last_name: 'asc',
        notifications_center: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Nom client', slot: 'client_name', class: 'col-1' },
    { label: 'Raison contact', slot: 'contact_reason', class: 'col-1' },
    { label: 'Véhicule', slot: 'vehicle', class: 'col-2' },
    { label: 'Prestation', slot: 'car_stage', class: 'col-1' },
    { label: 'Notifications', field: 'notifications_center', sortable: true, slot: 'notifications', class: 'col-1 text-center' },
    { label: 'Lu', slot: 'last_message_read', class: 'col-1 text-center' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Statut', slot: 'status', class: 'col-1' },
    { label: 'Dernière MAJ', field: 'updated_at', sortable: true, slot: 'updated_at', class: 'col-1' },
    { label: 'Centre', slot: 'center', class: 'col-1' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

// Filters
const errors = ref(null)
const filters = reactive({
    center_id: null,
    status: null,
    tag: null,
    read_status: null,
    date_from: null,
    date_to: null,
})
const filtersApplied = ref(true)
const isInitialized = ref(false)

// Period filter (sur la date de création de la demande)
const period = ref(null)
const periodOptions = [
    { text: 'Toutes les périodes', value: null },
    { text: 'Cette semaine', value: 'this_week' },
    { text: 'Ce mois-ci', value: 'this_month' },
    { text: 'Cette année', value: 'this_year' },
    { text: 'Année précédente', value: 'last_year' },
    { text: 'Période personnalisée', value: 'custom' },
]
const isCustomPeriod = computed(() => period.value === 'custom')

// Infobulles de l'application (floating-vue), une ligne par information
const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))
const tooltipLines = (lines) => ({ content: lines.filter(Boolean).map(escapeHtml).join('<br>'), html: true })

// Alerte « e-mail non arrivé » : statut, raison donnée par l'envoi, date
const emailProblemTooltip = (problem) => tooltipLines([problem.email_status_label, problem.email_error, problem.email_status_at_fr])

function formatDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function computePeriodRange(value) {
    const now = new Date()

    switch (value) {
        case 'this_week': {
            const dayOfWeek = now.getDay()
            const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
            const monday = new Date(now)
            monday.setDate(now.getDate() + diffToMonday)
            const sunday = new Date(monday)
            sunday.setDate(monday.getDate() + 6)
            return { from: formatDate(monday), to: formatDate(sunday) }
        }
        case 'this_month': {
            const from = new Date(now.getFullYear(), now.getMonth(), 1)
            const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
            return { from: formatDate(from), to: formatDate(to) }
        }
        case 'this_year':
            return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` }
        case 'last_year': {
            const year = now.getFullYear() - 1
            return { from: `${year}-01-01`, to: `${year}-12-31` }
        }
        default:
            return { from: null, to: null }
    }
}

function onPeriodChange(value) {
    period.value = value

    if (value === 'custom') {
        return
    }

    const { from, to } = computePeriodRange(value)
    filters.date_from = from
    filters.date_to = to
}

function applyFilter() {
    updateFilters({
        center_id: filters.center_id,
        status: filters.status,
        tag: filters.tag,
        read_status: filters.read_status,
        date_from: filters.date_from,
        date_to: filters.date_to,
    })
    filtersApplied.value = true
    saveFiltersToLocalStorage()
    loadData()
}

function saveFiltersToLocalStorage() {
    localStorage.setItem('clientRequestsFilters', JSON.stringify(filters))
    localStorage.setItem('clientRequestsPeriod', period.value ?? '')
    localStorage.setItem('clientRequestsFiltersApplied', filtersApplied.value.toString())
}

watch(
    () => [filters.center_id, filters.status, filters.tag, filters.read_status, filters.date_from, filters.date_to],
    () => {
        if (isInitialized.value) {
            filtersApplied.value = false
        }
    },
    { deep: true }
)

// Centers for filter
const centers = ref([])
function getCenters() {
    centers.value.push({ text: 'Tous les centres', value: null })
    axios.get('/api/centers?sortingField=name&sortingOrder=asc&all==true')
        .then(response => {
            response.data.data.forEach(center => {
                centers.value.push({
                    text: center.data.name,
                    value: center.data.id
                })
            })
        })
        .catch(error => {
            httpErrorsHandler(error)
        })
}

// LocalStorage - Load and Save
onBeforeMount(() => {
    getCenters()

    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
    }

    // Load filters from localStorage
    const savedFilters = localStorage.getItem('clientRequestsFilters')
    if (savedFilters) {
        Object.assign(filters, JSON.parse(savedFilters))
        updateFilters({
            center_id: filters.center_id,
            status: filters.status,
            tag: filters.tag,
            read_status: filters.read_status,
            date_from: filters.date_from,
            date_to: filters.date_to,
        })
    }
    const savedPeriod = localStorage.getItem('clientRequestsPeriod')
    period.value = savedPeriod ? savedPeriod : null
    filtersApplied.value = localStorage.getItem('clientRequestsFiltersApplied') !== 'false'

    loadData(url.value)
})

onMounted(() => {
    isInitialized.value = true
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
        })
    },
    { deep: true }
)

// Modal management
onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToDelete = ref(null)
const idToDuplicate = ref(null)

function showModal(modalId, id = null) {
    if (modalId === 'modalDelete') {
        idToDelete.value = id
    } else if (modalId === 'modalDuplicate') {
        idToDuplicate.value = id
    }
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

// Composable for CRUD operations
const {
    updatingReasonForLoss,
    deleteClientRequest,
    duplicateClientRequest,
    changeStatus: changeStatusApi,
    updateReasonForLoss: updateReasonForLossApi,
} = useClientRequests({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onDuplicated: (data) => {
        loadData(url.value)
        hideModal('modalDuplicate')
        router.push({ name: 'client-requests.show', params: { id: data.id } })
    },
    onStatusChanged: (id, status) => {
        const index = clientRequests.value.findIndex(cr => cr.data.id === id)
        if (index !== -1) {
            const statusInfo = clientRequestStatuses.find(s => s.value === status)
            clientRequests.value[index].data.status = status
            clientRequests.value[index].data.status_label_fr = statusInfo.label
            clientRequests.value[index].data.status_badge_color = statusInfo.badgeColor
            // Demande traitée : sa bulle disparaît (comme côté serveur, pour tout le centre)
            if (HANDLED_STATUSES.includes(status)) {
                clientRequests.value[index].data.notifications_center = 0
            }
        }
    },
    onReasonForLossUpdated: (id, data) => {
        const index = clientRequests.value.findIndex(cr => cr.data.id === id)
        if (index !== -1) {
            const statusInfo = clientRequestStatuses.find(s => s.value === data.status)
            clientRequests.value[index].data.status = data.status
            clientRequests.value[index].data.status_label_fr = statusInfo.label
            clientRequests.value[index].data.status_badge_color = statusInfo.badgeColor
            clientRequests.value[index].data.notifications_center = 0
        }
        hideModal('modalReasonForLoss')
        formReasonForLoss.reason_for_loss = null
    },
    onError: (error) => httpErrorsHandler(error),
})

// Status management
const clientRequestStatuses = reactive([
    { value: 'pending', label: 'À traiter', badgeColor: 'danger' },
    { value: 'responded', label: 'Répondu', badgeColor: 'secondary' },
    { value: 'parts_ordered', label: 'Pièces commandées', badgeColor: 'info' },
    { value: 'appointment_scheduled', label: 'RDV programmé', badgeColor: 'success' },
    { value: 'appointment_ongoing', label: 'RDV en cours', badgeColor: 'success' },
    { value: 'won', label: 'Gagné', badgeColor: 'success' },
    { value: 'no_show', label: 'Client non présenté', badgeColor: 'dark' },
    { value: 'lost', label: 'Perdu', badgeColor: 'dark' },
    { value: 'closed', label: 'Fermé', badgeColor: 'dark' },
])

const clientRequestIdForLoss = ref(null)

function handleStatusChange(status, item) {
    if (status === item.data.status) {
        return
    }
    if (status === 'lost') {
        clientRequestIdForLoss.value = item.data.id
        showModal('modalReasonForLoss')
        return
    }
    changeStatusApi(item.data.id, status)
}

// Reason for loss
const formReasonForLoss = reactive({
    reason_for_loss: null,
})

function submitReasonForLoss() {
    updateReasonForLossApi(clientRequestIdForLoss.value, formReasonForLoss.reason_for_loss)
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <div class="row g-3 align-items-center">
                        <div class="col-xxl-5 col-sm-12">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher une demande client..." v-model="query" @input="debouncedSearch" id="searchClientRequests">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-md-auto ms-auto">
                            <button type="button" class="btn me-2" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                <i class="ri-equalizer-fill me-1 align-bottom hide-on-mobile"></i> Filtrer
                            </button>
                            <router-link :to="{ name:'client-requests.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_client_request">
                                <i class="ri-add-fill me-1 align-bottom hide-on-mobile"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>

                <!-- Filters -->
                <div class="card-body">
                    <div class="row g-3">
                        <form-select-field
                            name="filters.status"
                            label="Statut"
                            :label-hidden="true"
                            @update:field="filters.status = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.status"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les statuts"
                            :liste-options="[
                                { text: 'Statuts ouverts (A traiter, Répondu...)', value: 'all_open' },
                                { text: 'Statuts fermés (Fermé, Client non présenté...)', value: 'all_closed' },
                                { text: 'Tous les statuts', value: null },
                                { text: '---', value: null },
                                { text: 'À traiter', value: 'pending' },
                                { text: 'Répondu', value: 'responded' },
                                { text: 'Pièces commandées', value: 'parts_ordered' },
                                { text: 'RDV programmé', value: 'appointment_scheduled' },
                                { text: 'RDV en cours', value: 'appointment_ongoing' },
                                { text: 'Gagné', value: 'won' },
                                { text: 'Client non présenté', value: 'no_show' },
                                { text: 'Perdu', value: 'lost' },
                                { text: 'Fermé', value: 'closed' }
                            ]"
                            margin="mb-0"
                        />

                        <form-select-field
                            name="filters.tag"
                            label="Tag"
                            :label-hidden="true"
                            @update:field="filters.tag = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.tag"
                            bs-class="col-xxl-2 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les tags"
                            :liste-options="[
                                { text: 'Tous les tags', value: null },
                                { text: '---', value: null },
                                { text: 'Promotion', value: 'Promotion' },
                                { text: 'ECU en dev', value: 'ECU en dev' },
                                { text: 'SAV', value: 'SAV' },
                                { text: 'Remise Prépa', value: 'Remise Prépa' },
                                { text: 'Remise origine', value: 'Remise origine' },
                                { text: 'Franchise', value: 'Franchise' },
                                { text: 'Jobs', value: 'Jobs' },
                                { text: 'Jeux-concours', value: 'Jeux-concours' },
                                { text: 'Doublon', value: 'Doublon' },
                            ]"
                            margin="mb-0"
                        />

                        <form-select-field
                            name="filters.read_status"
                            label="Lecture"
                            :label-hidden="true"
                            @update:field="filters.read_status = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.read_status"
                            bs-class="col-xxl-2 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Lu / non lu"
                            :liste-options="[
                                { text: 'Lu / non lu', value: null },
                                { text: '---', value: null },
                                { text: 'Lu par le client', value: 'read' },
                                { text: 'Non lu par le client', value: 'unread' },
                            ]"
                            margin="mb-0"
                        />

                        <form-select-field
                            name="filters.center_id"
                            label="Centre"
                            :label-hidden="true"
                            @update:field="filters.center_id = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.center_id"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les centres"
                            :liste-options="centers"
                            margin="mb-0"
                        />

                        <form-select-field
                            name="period"
                            label="Période"
                            :label-hidden="true"
                            @update:field="onPeriodChange($event)"
                            :errors="errors"
                            :required="true"
                            :data="period"
                            bs-class="col-xxl-2 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Toutes les périodes"
                            :liste-options="periodOptions"
                            margin="mb-0"
                        />
                    </div>

                    <!-- Période personnalisée -->
                    <div class="row g-3 mt-1" v-if="isCustomPeriod">
                        <form-date-picker
                            name="date_from"
                            label="Date de début"
                            :label-hidden="true"
                            @update:field="filters.date_from = $event"
                            :errors="errors"
                            :data="filters.date_from"
                            bs-class="col-xxl-6 col-sm-6"
                            placeholder="Date de début"
                        />

                        <form-date-picker
                            name="date_to"
                            label="Date de fin"
                            :label-hidden="true"
                            @update:field="filters.date_to = $event"
                            :errors="errors"
                            :data="filters.date_to"
                            bs-class="col-xxl-6 col-sm-6"
                            placeholder="Date de fin"
                        />
                    </div>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="clientRequests"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Nom client -->
                        <template #client_name="{ item }">
                            {{ item.data.client.data.last_name }} {{ item.data.client.data.first_name }}
                        </template>

                        <!-- Raison contact -->
                        <template #contact_reason="{ item }">
                            {{ item.data.contact_reason }}
                        </template>

                        <!-- Véhicule -->
                        <template #vehicle="{ item }">
                            {{ item.data.vehicle_brand_name }} - {{ item.data.vehicle_model_name }}
                        </template>

                        <!-- Prestation -->
                        <template #car_stage="{ item }">
                            {{ item.data.car_stage }}
                        </template>

                        <!-- Notifications -->
                        <template #notifications="{ item }">
                            <span v-if="item.data.notifications_center > 0" class="badge fs-11 rounded-pill bg-danger-subtle text-danger">
                                {{ item.data.notifications_center }}
                            </span>
                        </template>

                        <!-- Lecture du dernier message envoyé au client -->
                        <template #last_message_read="{ item }">
                            <span v-if="item.data.last_message_email_problem" class="text-danger" v-tooltip="emailProblemTooltip(item.data.last_message_email_problem)">
                                <i class="align-bottom fs-16" :class="item.data.last_message_email_problem.email_status_icon"></i>
                            </span>
                            <span v-else-if="item.data.last_message_read === true" class="text-success" v-tooltip="tooltipLines(['Lu le : ' + item.data.last_message_read_at_fr])">
                                <i class="ri-check-double-line align-bottom fs-16"></i>
                            </span>
                            <span v-else-if="item.data.last_message_read === false" class="text-muted" v-tooltip="'Non lu par le client'">
                                <i class="ri-check-line align-bottom fs-16"></i>
                            </span>
                        </template>

                        <!-- Détails -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'client-requests.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Accéder
                            </router-link>
                        </template>

                        <!-- Statut -->
                        <template #status="{ item }">
                            <span :class="'badge fs-11 bg-'+item.data.status_badge_color+'-subtle text-'+item.data.status_badge_color">
                                {{ item.data.status_label_fr }}
                            </span>
                            <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info ms-1" id="statusDropdown" data-bs-toggle="dropdown" v-if="item.can.update"></a>
                            <ul class="dropdown-menu" aria-labelledby="statusDropdown">
                                <li v-for="(status, key) in clientRequestStatuses" :key="key">
                                    <a class="dropdown-item" href="#" @click.prevent="handleStatusChange(status.value, item)">
                                        <span :class="`fs-11 badge bg-${status.badgeColor}-subtle text-${status.badgeColor}`">{{ status.label }}</span>
                                    </a>
                                </li>
                            </ul>
                        </template>

                        <!-- Dernière MAJ -->
                        <template #updated_at="{ item }">
                            {{ item.data.updated_at_fr }}
                        </template>

                        <!-- Centre -->
                        <template #center="{ item }">
                            {{ item.data.center.data.name }}
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'client-requests.edit', params: { id: item.data.id } }" class="text-info" title="Modifier la demande" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DUPLICATE -->
                            <a href="#" class="text-secondary" title="Dupliquer la demande" @click.prevent="showModal('modalDuplicate', item.data.id)" v-if="item.can.update">
                                <i class="ri-file-copy-line fs-17 px-1"></i>
                            </a>
                            <i class="ri-file-copy-line fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer la demande" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- WORKSHEET -->
                            <a :href="item.data.worksheet_path" class="text-success" title="Fiche de travail" target="_blank" v-if="item.data.worksheet_signed_path !== null">
                                <i class="ri-file-edit-fill fs-17 px-1"></i>
                            </a>
                            <a :href="item.data.worksheet_path" class="text-dark" title="Fiche de travail" target="_blank" v-else-if="item.data.worksheet_path !== null">
                                <i class="ri-file-edit-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-file-edit-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <!-- Modal Delete -->
                    <modal id="modalDelete" title="Supprimer la demande client ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteClientRequest(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer une demande client.</p>
                        </template>
                    </modal>

                    <!-- Modal Duplicate -->
                    <modal id="modalDuplicate" title="Dupliquer la demande ?" btn-class="btn-secondary" btn-text="Dupliquer" @click:button="duplicateClientRequest(idToDuplicate)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/uecgmesg.json" trigger="loop" colors="primary:#212529,secondary:#3080e8" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de dupliquer la demande client <strong>#{{ idToDuplicate }}</strong>. Confirmer la duplication ?</p>
                        </template>
                    </modal>

                    <!-- Modal Reason for Loss -->
                    <div id="modalReasonForLoss" class="modal fade" tabindex="-1" aria-labelledby="modalReasonForLossLabel" aria-hidden="true" style="display: none;">
                        <div class="modal-dialog modal-lg">
                            <loader v-if="updatingReasonForLoss"></loader>
                            <form v-on:submit.prevent="submitReasonForLoss" v-show="!updatingReasonForLoss">
                                <div class="modal-content">
                                    <div class="modal-header">
                                        <h5 class="modal-title" id="modalReasonForLossLabel">Raison de la perte</h5>
                                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                    </div>
                                    <div class="modal-body">
                                        <form-select-field
                                            name="reason_for_loss"
                                            label="Raison de la perte"
                                            :label-hidden="false"
                                            @update:field="formReasonForLoss.reason_for_loss = $event"
                                            :errors="errors"
                                            :required="true"
                                            :data="formReasonForLoss.reason_for_loss"
                                            bs-class="col-xl-12"
                                            :disabled="false"
                                            :liste-options="[
                                                { text: 'Doublon', value: 'Doublon' },
                                                { text: 'Plus de réponse', value: 'Plus de réponse' },
                                                { text: 'Prix', value: 'Prix' },
                                                { text: 'Concurrent', value: 'Concurrent' },
                                                { text: 'Délais', value: 'Délais' },
                                                { text: 'Perte de confiance', value: 'Perte de confiance' },
                                                { text: 'Hors sujet', value: 'Hors sujet' },
                                                { text: 'Garantie', value: 'Garantie' },
                                                { text: 'Assurance', value: 'Assurance' },
                                            ]"
                                        />
                                    </div>
                                    <div class="modal-footer">
                                        <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                                        <button type="submit" class="btn btn-secondary">Valider</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
